import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import {
  uploadMaterialsFileWithProgress,
  type MaterialsItem,
} from "@/lib/materials-api";

export type MaterialsUploadJobStatus = "queued" | "uploading" | "done" | "error";

export type MaterialsUploadJob = {
  id: string;
  batchId: string;
  accountId: string;
  fileName: string;
  byteSize: number;
  folderId: number | null;
  brandId: number | null;
  materialsPath: string;
  status: MaterialsUploadJobStatus;
  /** 当前文件上传进度 0–1 */
  progress: number;
  error?: string;
};

export type EnqueueMaterialsUploadsInput = {
  accountId: string;
  files: File[];
  folderId?: number | null;
  brandId?: number | null;
  /** 完成后 toast「查看」跳转路径 */
  materialsPath: string;
};

type MaterialsUploadQueueValue = {
  jobs: MaterialsUploadJob[];
  activeCount: number;
  enqueueUploads: (input: EnqueueMaterialsUploadsInput) => void;
  dismissIdle: () => void;
  /** 某账户有文件上传成功时回调（用于素材页 / 选资源器静默刷新） */
  subscribeItemDone: (
    listener: (accountId: string, item: MaterialsItem) => void,
  ) => () => void;
};

const MaterialsUploadQueueContext = createContext<MaterialsUploadQueueValue | null>(null);

let jobSeq = 0;
let batchSeq = 0;

function nextJobId() {
  jobSeq += 1;
  return `mat-up-${Date.now()}-${jobSeq}`;
}

function nextBatchId() {
  batchSeq += 1;
  return `mat-batch-${Date.now()}-${batchSeq}`;
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

export function MaterialsUploadProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<MaterialsUploadJob[]>([]);

  const pumpingRef = useRef(false);
  const pendingIdsRef = useRef<string[]>([]);
  const fileByJobIdRef = useRef(new Map<string, File>());
  const jobMetaRef = useRef(
    new Map<
      string,
      {
        batchId: string;
        accountId: string;
        folderId: number | null;
        brandId: number | null;
      }
    >(),
  );
  const batchMetaRef = useRef(
    new Map<string, { materialsPath: string; total: number; done: number; failed: number }>(),
  );
  const itemDoneListenersRef = useRef(new Set<(accountId: string, item: MaterialsItem) => void>());

  const updateJob = useCallback((id: string, patch: Partial<MaterialsUploadJob>) => {
    setJobs((prev) => prev.map((job) => (job.id === id ? { ...job, ...patch } : job)));
  }, []);

  const finishBatchIfComplete = useCallback(
    (batchId: string) => {
      const meta = batchMetaRef.current.get(batchId);
      if (!meta) return;
      if (meta.done + meta.failed < meta.total) return;

      const { materialsPath, total, done, failed } = meta;
      batchMetaRef.current.delete(batchId);

      if (done === 0) {
        toast.error(failed === 1 ? "上传失败" : `${failed} 个文件上传失败`);
        return;
      }

      const description =
        failed > 0
          ? `成功 ${done} 个，失败 ${failed} 个`
          : total === 1
            ? "可在素材库中查看"
            : `共 ${done} 个文件`;

      toast.success(total === 1 ? "上传完成" : `${done} 个文件已上传`, {
        description,
        action: {
          label: "查看素材库",
          onClick: () => navigate(materialsPath),
        },
      });
    },
    [navigate],
  );

  const pump = useCallback(async () => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    try {
      while (pendingIdsRef.current.length > 0) {
        const id = pendingIdsRef.current.shift()!;
        const file = fileByJobIdRef.current.get(id);
        const meta = jobMetaRef.current.get(id);
        if (!file || !meta) {
          updateJob(id, { status: "error", error: "文件已失效", progress: 0 });
          if (meta) {
            const batch = batchMetaRef.current.get(meta.batchId);
            if (batch) {
              batch.failed += 1;
              finishBatchIfComplete(meta.batchId);
            }
            jobMetaRef.current.delete(id);
          }
          continue;
        }

        updateJob(id, { status: "uploading", progress: 0 });

        const form = new FormData();
        form.append("file", file);
        form.append("name", file.name);
        if (meta.folderId != null) form.append("folder_id", String(meta.folderId));
        if (meta.brandId != null) form.append("brand_id", String(meta.brandId));

        try {
          const payload = await uploadMaterialsFileWithProgress(meta.accountId, form, {
            onProgress: (ratio) => updateJob(id, { progress: ratio }),
          });
          updateJob(id, { status: "done", progress: 1 });
          fileByJobIdRef.current.delete(id);
          jobMetaRef.current.delete(id);
          itemDoneListenersRef.current.forEach((listener) =>
            listener(meta.accountId, payload.item),
          );
          const batch = batchMetaRef.current.get(meta.batchId);
          if (batch) {
            batch.done += 1;
            finishBatchIfComplete(meta.batchId);
          }
        } catch (caught) {
          updateJob(id, {
            status: "error",
            error: errorMessage(caught, "上传失败"),
            progress: 0,
          });
          fileByJobIdRef.current.delete(id);
          jobMetaRef.current.delete(id);
          const batch = batchMetaRef.current.get(meta.batchId);
          if (batch) {
            batch.failed += 1;
            finishBatchIfComplete(meta.batchId);
          }
        }
      }
    } finally {
      pumpingRef.current = false;
      if (pendingIdsRef.current.length > 0) {
        void pump();
      }
    }
  }, [finishBatchIfComplete, updateJob]);

  const enqueueUploads = useCallback(
    (input: EnqueueMaterialsUploadsInput) => {
      if (input.files.length === 0) return;

      const batchId = nextBatchId();
      batchMetaRef.current.set(batchId, {
        materialsPath: input.materialsPath,
        total: input.files.length,
        done: 0,
        failed: 0,
      });

      const created: MaterialsUploadJob[] = input.files.map((file) => {
        const id = nextJobId();
        fileByJobIdRef.current.set(id, file);
        jobMetaRef.current.set(id, {
          batchId,
          accountId: input.accountId,
          folderId: input.folderId ?? null,
          brandId: input.brandId ?? null,
        });
        pendingIdsRef.current.push(id);
        return {
          id,
          batchId,
          accountId: input.accountId,
          fileName: file.name,
          byteSize: file.size,
          folderId: input.folderId ?? null,
          brandId: input.brandId ?? null,
          materialsPath: input.materialsPath,
          status: "queued" as const,
          progress: 0,
        };
      });

      setJobs((prev) => [...prev, ...created]);
      void pump();
    },
    [pump],
  );

  const dismissIdle = useCallback(() => {
    setJobs((prev) =>
      prev.filter((job) => job.status === "queued" || job.status === "uploading"),
    );
  }, []);

  const subscribeItemDone = useCallback(
    (listener: (accountId: string, item: MaterialsItem) => void) => {
      itemDoneListenersRef.current.add(listener);
      return () => {
        itemDoneListenersRef.current.delete(listener);
      };
    },
    [],
  );

  useEffect(() => {
    const busy = jobs.some((job) => job.status === "queued" || job.status === "uploading");
    if (busy) return;
    if (jobs.length === 0) return;
    const timer = window.setTimeout(() => dismissIdle(), 4000);
    return () => window.clearTimeout(timer);
  }, [jobs, dismissIdle]);

  const activeCount = useMemo(
    () => jobs.filter((job) => job.status === "queued" || job.status === "uploading").length,
    [jobs],
  );

  const value = useMemo(
    () => ({
      jobs,
      activeCount,
      enqueueUploads,
      dismissIdle,
      subscribeItemDone,
    }),
    [jobs, activeCount, enqueueUploads, dismissIdle, subscribeItemDone],
  );

  return (
    <MaterialsUploadQueueContext.Provider value={value}>
      {children}
    </MaterialsUploadQueueContext.Provider>
  );
}

export function useMaterialsUploadQueue(): MaterialsUploadQueueValue {
  const ctx = useContext(MaterialsUploadQueueContext);
  if (!ctx) {
    throw new Error("useMaterialsUploadQueue must be used within MaterialsUploadProvider");
  }
  return ctx;
}

export function useOptionalMaterialsUploadQueue(): MaterialsUploadQueueValue | null {
  return useContext(MaterialsUploadQueueContext);
}
